import { useCallback, useEffect, useRef } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import useNavTopBar from "@organisms/NavTopBar/NavTopBar.context"

interface UseNavTopBarMobileTabsReturn {
    mobileTabsRef: (element: HTMLDivElement | null) => void
}

const MOBILE_TABS_SNAP_DELAY_MS = 150

export default function useNavTopBarMobileTabs(): UseNavTopBarMobileTabsReturn {
    const { setMobileTabsRef } = useNavTopBar()

    const isDesktop = useIsDesktop()

    const translateYRef = useRef(0)
    const tabsNodeRef = useRef<HTMLDivElement | null>(null)

    const mobileTabsRef = useCallback(
        (element: HTMLDivElement | null) => {
            tabsNodeRef.current = element
            setMobileTabsRef(element)
        },
        [setMobileTabsRef],
    )

    useEffect(() => {
        const element = tabsNodeRef.current

        if (isDesktop) {
            translateYRef.current = 0

            if (element) {
                element.style.transition = "none"
                element.style.transform = "translateY(0px)"
            }

            return
        }

        let lastScrollY = window.scrollY
        let snapTimeout: ReturnType<typeof setTimeout>

        const handleScroll = () => {
            const currentElement = tabsNodeRef.current

            if (!currentElement) {
                return
            }

            const tabsHeight = currentElement.offsetHeight

            if (tabsHeight === 0) {
                return
            }

            const currentScrollY = window.scrollY
            const scrollDelta = currentScrollY - lastScrollY

            lastScrollY = currentScrollY

            clearTimeout(snapTimeout)

            const nextTranslateY = Math.max(-tabsHeight, Math.min(0, translateYRef.current - scrollDelta))

            translateYRef.current = nextTranslateY
            currentElement.style.transition = "none"
            currentElement.style.transform = `translateY(${nextTranslateY}px)`

            snapTimeout = setTimeout(() => {
                const snappedTranslateY = translateYRef.current < -tabsHeight / 2 ? -tabsHeight : 0

                translateYRef.current = snappedTranslateY
                currentElement.style.transition = "transform var(--duration-moderate) ease"
                currentElement.style.transform = `translateY(${snappedTranslateY}px)`
            }, MOBILE_TABS_SNAP_DELAY_MS)
        }

        window.addEventListener("scroll", handleScroll, { passive: true })

        return () => {
            window.removeEventListener("scroll", handleScroll)
            clearTimeout(snapTimeout)
        }
    }, [isDesktop])

    return {
        mobileTabsRef,
    }
}
